/**
 * `localStorage`, without the ability to take the app down.
 *
 * Reading `window.localStorage` THROWS a `SecurityError` — it does not
 * return null — whenever the browser is set to block site data: Safari's
 * "Block All Cookies", a per-site block in Chrome or Edge, Brave's
 * shields, an enterprise policy, an embedded webview with no same-origin
 * storage. Those are ordinary browsers that ordinary people report from.
 *
 * Unguarded, that throw lands wherever the access happens to be. In a
 * render body or a `useState` initializer it reaches the root
 * ErrorBoundary and replaces the whole app with "Something went wrong";
 * at module scope it kills the bundle before React starts. Either way,
 * over a remembered preference.
 *
 * Every stored value here is a convenience — a dismissed banner, a theme
 * choice, the last city. Losing one is nothing. So a blocked store reads
 * as "nothing remembered" and writes go nowhere, which is exactly the
 * behaviour a reader who blocks storage is asking for.
 */
export const safeStorage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Nothing to remember. Not a reason to break the page.
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // See `set`.
    }
  },
};
